import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { InputComponent } from '@libs/front/components/input/input.component';
import { ScrollableComponent } from 'src/app/shared/components/scrollable';
import { DeliveryActivityItem, DeliveryBlastRow } from 'src/app/shared/models/delivery-activity.models';
import { distinctHslColor } from 'src/app/utils/color.utils';
import { ActivityCardField, ActivityItem, ActivityItemClick, DrillColorBy, TimeScale } from '../../../planning.types';
import { ActivityCardComponent } from '../../../ui/activity-card/activity-card.component';
import { ActivityCellComponent } from '../../../ui/activity-cell/activity-cell.component';
import { ProgressRingComponent } from '../../../ui/progress-ring/progress-ring.component';
import { ShiftIconComponent } from '../../../ui/shift-icon/shift-icon.component';
import { CARD_POSITIONS, formatDisplayDate, matchesSearch } from '../../../utils/grid-helpers';
import { DayGroup, ShiftColumn, generateDayGroups } from '../../../utils/column-generator';

interface RowTotals {
  loadedKg: number;
  holesLoaded: number;
  holeProgress: number;
}

/**
 * Grille de planning des livraisons : une ligne par tir, une colonne par shift.
 * Presentational — colonnes, période et sélection de shifts viennent du parent.
 */
@Component({
  selector: 'app-delivery-grid',
  standalone: true,
  templateUrl: './delivery-grid.component.html',
  styleUrls: ['../../../ui/planning-grid.scss'],
  imports: [
    DecimalPipe,
    FormsModule,
    OverlayModule,
    TranslateModule,
    InputComponent,
    ScrollableComponent,
    ActivityCardComponent,
    ActivityCellComponent,
    ProgressRingComponent,
    ShiftIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryGridComponent {
  readonly rows             = input<DeliveryBlastRow[]>([]);
  readonly shiftColumns     = input.required<ShiftColumn[]>();
  readonly timeScale        = input<TimeScale>('week');
  readonly colorBy          = input<DrillColorBy>('rig');
  readonly selectedShiftIds = input<ReadonlySet<string>>(new Set<string>());

  readonly rowClick      = output<DeliveryBlastRow>();
  readonly deliveryClick = output<DeliveryActivityItem>();
  readonly shiftToggle   = output<ShiftColumn>();

  protected readonly el      = inject(ElementRef);
  private readonly translate = inject(TranslateService);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  protected readonly searchTerm   = signal('');
  protected readonly activeId     = signal<string | null>(null);
  protected readonly cardAnchor   = signal<HTMLElement | null>(null);
  protected readonly cardPositions: ConnectedPosition[] = CARD_POSITIONS;

  constructor() {
    // Le passage en vue mensuelle recentre la grille sur aujourd'hui.
    effect(() => {
      const scale = this.timeScale();
      untracked(() => {
        if (scale === 'month') {
          setTimeout(() => this.scrollToToday());
        }
      });
    });
  }

  protected readonly dayGroups = computed<DayGroup[]>(() =>
    generateDayGroups(this.shiftColumns(), this.timeScale(), this.lang() || undefined),
  );

  protected readonly cellWidthPx = computed<number>(() =>
    this.timeScale() === 'month' ? 32 : 57,
  );

  protected readonly visibleRows = computed<DeliveryBlastRow[]>(() => {
    const term = this.searchTerm();
    return this.rows().filter(row => matchesSearch(term, row.label, row.zone));
  });

  private readonly selectedDates = computed<Set<string>>(() => {
    const selected = this.selectedShiftIds();
    return new Set(this.shiftColumns().filter(col => selected.has(col.id)).map(col => col.date));
  });

  protected isShiftSelected(col: ShiftColumn): boolean {
    return this.selectedShiftIds().has(col.id);
  }

  protected isDaySelected(date: string): boolean {
    return this.selectedDates().has(date);
  }

  /**
   * Couleurs réparties sur le cercle chromatique par engin (ou opérateur)
   * distinct, comme dans l'écran d'origine ; le critère « rig » désigne ici l'engin.
   */
  private readonly colorByKey = computed(() => {
    const colorBy = this.colorBy();
    const labels  = new Set<string>();
    for (const row of this.rows()) {
      for (const d of row.deliveries) {
        const label = colorBy === 'rig' ? d.equipmentLabel : d.operatorLabel;
        if (label) { labels.add(label.toLowerCase()); }
      }
    }
    const sorted = [...labels].sort((a, b) => a.localeCompare(b));
    return new Map(sorted.map((key, index) => [key, distinctHslColor(index, sorted.length)]));
  });

  /** Blocs indexés par tir et par colonne, pour ne pas rebalayer chaque ligne par cellule. */
  private readonly itemsByRowAndColumn = computed(() => {
    const colorBy = this.colorBy();
    const colors  = this.colorByKey();
    const index   = new Map<number, Map<string, ActivityItem[]>>();

    for (const row of this.rows()) {
      const byColumn = new Map<string, ActivityItem[]>();
      const sorted   = [...row.deliveries].sort((a, b) => a.equipmentLabel.localeCompare(b.equipmentLabel));

      for (const d of sorted) {
        const label = colorBy === 'rig' ? d.equipmentLabel : d.operatorLabel;
        const item: ActivityItem = {
          id:    d.id,
          value: `${Math.round(d.loadedKg).toLocaleString(this.lang() || undefined)}kg`,
          label,
          color: colors.get(label.toLowerCase()) ?? distinctHslColor(0, 1),
          date:  d.date,
        };

        const key  = `${d.date}_${d.shiftIndex}`;
        const list = byColumn.get(key);
        if (list) { list.push(item); } else { byColumn.set(key, [item]); }
      }
      index.set(row.blastId, byColumn);
    }
    return index;
  });

  protected getItemsForCell(row: DeliveryBlastRow, col: ShiftColumn): ActivityItem[] {
    return this.itemsByRowAndColumn().get(row.blastId)?.get(col.id) ?? [];
  }

  /** Kilos chargés par colonne, sur les tirs affichés. */
  private readonly shiftTotals = computed(() => {
    const totals = new Map<string, number>();
    for (const row of this.visibleRows()) {
      for (const d of row.deliveries) {
        const key = `${d.date}_${d.shiftIndex}`;
        totals.set(key, (totals.get(key) ?? 0) + d.loadedKg);
      }
    }
    return totals;
  });

  protected getShiftTotal(col: ShiftColumn): number {
    return this.shiftTotals().get(col.id) ?? 0;
  }

  /**
   * Réalisé et avancement par tir. L'avancement se mesure en trous chargés :
   * la cible en kg n'est pas fournie par l'API (`targetKg` vaut toujours 0).
   */
  private readonly rowTotals = computed(() => {
    const totals = new Map<number, RowTotals>();
    for (const row of this.rows()) {
      const loadedKg    = row.deliveries.reduce((sum, d) => sum + d.loadedKg, 0);
      const holesLoaded = row.deliveries.reduce((sum, d) => sum + (d.holeCount ?? 0), 0);
      totals.set(row.blastId, {
        loadedKg,
        holesLoaded,
        holeProgress: row.blastHoleCount ? (holesLoaded / row.blastHoleCount) * 100 : 0,
      });
    }
    return totals;
  });

  protected getTotals(row: DeliveryBlastRow): RowTotals {
    return this.rowTotals().get(row.blastId) ?? { loadedKg: 0, holesLoaded: 0, holeProgress: 0 };
  }

  /** Borne un pourcentage à [0, 100] pour la largeur CSS de la barre. */
  protected clamp100(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  /** Avancement global des tirs affichés, en trous chargés, et kilos cumulés. */
  protected readonly overall = computed(() => {
    let holesLoaded = 0;
    let holes       = 0;
    let loadedKg    = 0;
    for (const row of this.visibleRows()) {
      const totals = this.getTotals(row);
      holesLoaded += totals.holesLoaded;
      loadedKg    += totals.loadedKg;
      holes       += row.blastHoleCount ?? 0;
    }
    return { holesLoaded, holes, loadedKg, percent: holes ? (holesLoaded / holes) * 100 : 0 };
  });

  private readonly deliveryById = computed(() => {
    const index = new Map<string, DeliveryActivityItem>();
    for (const row of this.rows()) {
      for (const d of row.deliveries) { index.set(d.id, d); }
    }
    return index;
  });

  protected readonly activeDelivery = computed<DeliveryActivityItem | null>(() => {
    const id = this.activeId();
    return id ? this.deliveryById().get(id) ?? null : null;
  });

  protected readonly cardFields = computed<ActivityCardField[]>(() => {
    const d = this.activeDelivery();
    if (!d) { return []; }
    return [
      { label: this.translate.instant('PLANNING.DATE'),         value: formatDisplayDate(d.date) },
      { label: this.translate.instant('PLANNING.LOADED'),       value: `${Math.round(d.loadedKg).toLocaleString(this.lang() || undefined)}kg` },
      { label: this.translate.instant('PLANNING.HOLES_LOADED'), value: String(d.holeCount ?? 0) },
      { label: this.translate.instant('PLANNING.EQUIPMENT'),    value: d.equipmentLabel },
      { label: this.translate.instant('PLANNING.OPERATOR'),     value: d.operatorLabel },
    ];
  });

  protected openCard(click: ActivityItemClick): void {
    this.cardAnchor.set(click.anchor);
    this.activeId.set(click.id);
  }

  protected closeCard(): void {
    this.activeId.set(null);
  }

  protected onSeeMore(): void {
    const delivery = this.activeDelivery();
    this.closeCard();
    if (delivery) { this.deliveryClick.emit(delivery); }
  }

  /** Centre horizontalement la colonne du jour dans la zone visible. */
  private scrollToToday(): void {
    const host    = this.el.nativeElement as HTMLElement;
    const wrapper = host.querySelector('.planning-grid__scroll .scrollable-content') as HTMLElement | null;
    if (!wrapper) { return; }

    const todayCell = wrapper.querySelector('tbody td.is-today') as HTMLElement | null;
    if (!todayCell) { return; }

    const sidebarWidth = (host.querySelector('.planning-grid__sticky-left') as HTMLElement | null)?.offsetWidth ?? 168;
    const visibleWidth = wrapper.clientWidth - sidebarWidth;
    const scrollLeft   = todayCell.offsetLeft - sidebarWidth - visibleWidth / 2 + todayCell.offsetWidth / 2;

    wrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }
}
